import { CheckIcon, ChevronsUpDown, Globe } from 'lucide-react'
import {
  type ComponentRef,
  type ForwardRefExoticComponent,
  type InputHTMLAttributes,
  forwardRef,
  useCallback
} from 'react'
import type { FlagProps, Props as RPNProps } from 'react-phone-number-input'
import flags from 'react-phone-number-input/flags'
import * as RPNInput from 'react-phone-number-input/max'

import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn } from '@/lib/utils'

type PhoneInputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'onChange' | 'value'
> &
  Omit<RPNProps<typeof RPNInput.default>, 'onChange'> & {
    onChange?: (value: string) => void
  }

/**
 * react-phone-number-input may call onChange with `undefined` when the value
 * isn't a valid phone yet. coerce to empty string so consumers never have to
 * branch on undefined.
 */
const PhoneInput: ForwardRefExoticComponent<PhoneInputProps> = forwardRef<
  ComponentRef<typeof RPNInput.default>,
  PhoneInputProps
>(({ className, onChange, ...props }, ref) => {
  return (
    <RPNInput.default
      ref={ref}
      className={cn('flex', className)}
      flagComponent={FlagComponent}
      countrySelectComponent={CountrySelect}
      inputComponent={InputComponent}
      onChange={value => onChange?.(value ?? '')}
      {...props}
    />
  )
})
PhoneInput.displayName = 'PhoneInput'

const InputComponent = forwardRef<
  HTMLInputElement,
  React.ComponentProps<'input'>
>(({ className, ...props }, ref) => {
  return (
    <Input
      className={cn('rounded-e-lg rounded-s-none', className)}
      autoComplete='tel'
      {...props}
      ref={ref}
      type='tel'
    />
  )
})
InputComponent.displayName = 'InputComponent'

type CountrySelectOption = { label: string; value: RPNInput.Country }

type CountrySelectProps = {
  disabled?: boolean
  value: RPNInput.Country
  onChange: (value: RPNInput.Country) => void
  options: CountrySelectOption[]
}

function CountrySelect({
  disabled = false,
  value,
  onChange,
  options
}: CountrySelectProps) {
  const handleSelect = useCallback(
    (country: RPNInput.Country) => {
      onChange(country)
    },
    [onChange]
  )

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type='button'
          variant='outline'
          className='flex min-w-16 justify-center gap-1 rounded-e-none rounded-s-lg px-3 sm:min-w-[60px]'
          disabled={disabled}
        >
          {value ? (
            <>
              <FlagComponent country={value} countryName={value} />
              <span className='text-xs text-muted-foreground'>
                +{RPNInput.getCountryCallingCode(value)}
              </span>
            </>
          ) : (
            <Globe className='h-4 w-4 text-muted-foreground' />
          )}
          <ChevronsUpDown
            className={cn(
              'ml-auto h-4 w-4 opacity-50',
              disabled ? 'hidden' : 'opacity-100'
            )}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-[calc(100vw-2rem)] max-w-[300px] p-0'>
        <Command>
          {/* outside the scroll area: nested in it the search box scrolls
              away as soon as the list moves. */}
          <CommandInput
            placeholder='Search country...'
            className='text-base sm:text-xs'
          />
          <CommandList>
            <ScrollArea className='h-72'>
              <CommandEmpty>No country found.</CommandEmpty>
              <CommandGroup>
                {options
                  .filter(x => x.value)
                  .map(option => (
                    <CommandItem
                      key={option.value}
                      className='gap-2'
                      onSelect={() => handleSelect(option.value)}
                    >
                      <FlagComponent
                        country={option.value}
                        countryName={option.label}
                      />
                      <span className='flex-1 text-sm'>{option.label}</span>
                      {option.value && (
                        <span className='text-sm text-foreground/50'>
                          {`+${RPNInput.getCountryCallingCode(option.value)}`}
                        </span>
                      )}
                      <CheckIcon
                        className={cn(
                          'ml-auto h-4 w-4',
                          option.value === value ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                    </CommandItem>
                  ))}
              </CommandGroup>
            </ScrollArea>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

function FlagComponent({ country, countryName }: FlagProps) {
  const Flag = flags[country]
  return <span>{Flag && <Flag title={countryName} />}</span>
}

export { PhoneInput }
export {
  isValidPhoneNumber,
  parsePhoneNumber
} from 'react-phone-number-input/max'
export type { Country } from 'react-phone-number-input'
